/* import __COLOCATED_TEMPLATE__ from './basket.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';

import type QuoteService from 'embercom/services/quote-service';
import type Quote from 'embercom/models/quote';
import { FIN_PRICE_PER_RESOLUTION } from 'embercom/lib/billing';

export interface Args {
  quote?: Quote;
  yearlyBillingPeriodSelected: boolean;
  showDiscount: boolean;
  selectedPlanId: string;
  seatNumber: number;
  additionalSeatNumber?: number;
  selectedProactiveAddon: boolean;
  selectedCopilotAddon: boolean;
  selectedNumberOfCopilotAdmins: number;
  hasSelectedAiInsightsAddon: boolean;
  fullSeatPrice: number;
  loading: boolean;
  discountedSeatPrice: number;
  fullProactiveAddonPrice: number;
  discountedProactiveAddonPrice: number;
  fullCopilotAddonPrice: number;
  discountedCopilotAddonPrice?: number;
  fullAiInsightsAddonPrice?: number;
  discountedAiInsightsAddonPrice?: number;
  monthlyFullSeatPrice: number;
  monthlyDiscountedSeatPrice: number;
  monthlyAdditionalSeatPrice?: number;
  monthlyDiscountedAdditionalSeatPrice?: number;
  monthlyDiscountedTotal: number;
  monthlyCopilotPriceForSeats: number;
  monthlyTotal: number;
  hasCopilotEarlyStageDiscount?: boolean;
  trialEndDate?: string;
  trialEndFullPrice?: number;
  trialEndDiscountPrice?: number;
  totalCopy: string;
  totalPriceFormatted: string;
  showAdditionalSeats?: boolean;
  monthlyResolutionAllowance?: number;
  isEarlyStage?: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
  Blocks: {
    header?: any;
    beforeTotalBlock: any;
    afterTotalBlock?: any;
    basketButton: any;
    topOfBasket?: any;
  };
}

export default class Basket extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: any;
  @service declare quoteService: QuoteService;

  get subHeading() {
    return this.intl.t(
      `signup.teams.pricing5.annual-plans.basket.subheading.${this.args.selectedPlanId}`,
    );
  }

  get finMetricCardDescription() {
    if (this.args.isEarlyStage) {
      if (this.appService.app.canUseOutcomesRenaming) {
        return 'signup.teams.pricing5.annual-plans.metrics.fin.description-outcome-early-stage';
      }
      return 'signup.teams.pricing5.annual-plans.metrics.fin.description-early-stage';
    }

    if (this.appService.app.canUseOutcomesRenaming) {
      return 'signup.teams.pricing5.annual-plans.metrics.fin.description-outcome';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.fin.description';
  }

  get finMetricCardTooltip() {
    if (this.appService.app.canUseOutcomesRenaming) {
      return 'signup.teams.pricing5.annual-plans.metrics.fin.tooltip-outcome';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.fin.tooltip';
  }

  get finMetricCardTooltipArticleTitle() {
    if (this.appService.app.canUseOutcomesRenaming) {
      return 'signup.teams.pricing5.annual-plans.metrics.fin.learn-more-about-outcomes';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.fin.what-counts-as-resolution';
  }

  get basketResolutionsLabel() {
    if (this.appService.app.canUseOutcomesRenaming) {
      return 'signup.teams.pricing5.annual-plans.basket.resolutions-allowance-outcome';
    }

    return 'signup.teams.pricing5.annual-plans.basket.resolutions-allowance';
  }

  get resolutionPrice() {
    return FIN_PRICE_PER_RESOLUTION;
  }

  get actualSeatPrice() {
    return this.args.showDiscount ? this.args.discountedSeatPrice : this.args.fullSeatPrice;
  }

  get isProactiveAddonDiscounted() {
    return this.args.fullProactiveAddonPrice > this.args.discountedProactiveAddonPrice;
  }

  get isAiInsightsAddonDiscounted() {
    return (
      this.args.fullAiInsightsAddonPrice !== undefined &&
      this.args.discountedAiInsightsAddonPrice !== undefined &&
      this.args.fullAiInsightsAddonPrice > this.args.discountedAiInsightsAddonPrice
    );
  }

  get yearlySavings() {
    return this.quoteService.formatPrice(
      this.args.monthlyTotal * 12 - this.args.monthlyDiscountedTotal * 12,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::Basket': typeof Basket;
  }
}
